import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  alpha,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  GlobalStyles,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  CheckCircleOutline,
  AdminPanelSettings,
  EmojiEvents,
  Description,
  Lock,
  People,
  AttachMoney,
  CalendarMonth,
  Assessment,
  Mail,
  Policy,
  AutoStories,
  ArrowForward,
  Menu as MenuIcon,
  Close as CloseIcon,
  Phone,
  Email,
  TrendingUp,
  School,
  NotificationsActive,
  ArrowOutward,
  CheckCircle,
  EventNote,
  LibraryBooks,
  BarChart,
  EditNote,
  FactCheck,
} from "@mui/icons-material";
import RequestDemoForm from "../components/RequestDemoForm";

/* =====================================================
   DESIGN TOKENS
   Elegant editorial theme tuned to the logo's lime+cyan.
   The hero/dark sections frame the logo perfectly.
   ===================================================== */
const C = {
  // Deep neutral foundation
  ink: "#0F1419",          // near-black with green undertone — main dark bg
  inkDeep: "#080B0E",      // deepest tone for pricing/contact accents
  inkSoft: "#1A2128",      // cards/elevated surfaces on dark

  // Light foundation
  parchment: "#F5F1E8",    // warm off-white — main light bg
  parchmentSoft: "#EDE7D7",
  hairline: "#DDD6C2",

  // Brand accents (refined from logo)
  emerald: "#3B7A57",      // sophisticated green derived from logo lime
  emeraldDeep: "#2A5A3F",
  emeraldGlow: "#7BAE5A",  // closer to logo lime, used sparingly
  cerulean: "#2B8FB8",     // refined cyan derived from logo
  ceruleanLight: "#5DB3D4",

  // Text
  text: "#1A1F26",
  textMuted: "#5A6470",
  textOnDark: "#F5F1E8",
  textOnDarkMuted: "rgba(245, 241, 232, 0.65)",
};

const FONT_DISPLAY = `'Fraunces', 'Playfair Display', Georgia, serif`;
const FONT_BODY = `'Inter', system-ui, -apple-system, sans-serif`;
const FONT_MONO = `'JetBrains Mono', 'Courier New', monospace`;

/* ---------- Eyebrow label ---------- */
const Eyebrow: React.FC<{ children: React.ReactNode; light?: boolean }> = ({
  children,
  light,
}) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 1.25,
      "&::before": {
        content: '""',
        width: 24,
        height: "1px",
        background: light ? C.emeraldGlow : C.emerald,
      },
    }}
  >
    <Typography
      sx={{
        fontFamily: FONT_MONO,
        fontSize: "0.7rem",
        fontWeight: 500,
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: light ? C.emeraldGlow : C.emerald,
      }}
    >
      {children}
    </Typography>
  </Box>
);

/* =====================================================
   HERO COMPOSITION — "Command Center"
   The main console is the anchor; every accent card overlaps
   or hugs its edges so the whole composition reads as ONE
   coherent product visualization rather than scattered chips.
   ===================================================== */
const HeroComposition: React.FC = () => {
  const [shown, setShown] = useState(false);
  const [activeModule, setActiveModule] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setShown(true), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!shown) return;
    const interval = setInterval(() => {
      setActiveModule((m) => (m + 1) % 4);
    }, 3200);
    return () => clearInterval(interval);
  }, [shown]);

  const modules = [
    { label: "Overview", icon: <BarChart sx={{ fontSize: 13 }} /> },
    { label: "Attendance", icon: <FactCheck sx={{ fontSize: 13 }} /> },
    { label: "Examinations", icon: <EmojiEvents sx={{ fontSize: 13 }} /> },
    { label: "Finance", icon: <AttachMoney sx={{ fontSize: 13 }} /> },
  ];

  const kpis = [
    { label: "Students", value: "2,847", delta: "+38", icon: <School sx={{ fontSize: 13 }} /> },
    { label: "Attendance", value: "94.2%", delta: "+1.8", icon: <FactCheck sx={{ fontSize: 13 }} /> },
    { label: "Avg Score", value: "87.4", delta: "+4.2", icon: <TrendingUp sx={{ fontSize: 13 }} /> },
  ];

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: { xs: "560px", sm: "600px", lg: "640px" },
        perspective: "1600px",
      }}
    >
      {/* Ambient halo behind the whole composition */}
      <Box
        sx={{
          position: "absolute",
          inset: "5% 0%",
          background: `radial-gradient(circle at 55% 45%, ${alpha(C.emeraldGlow, 0.18)} 0%, transparent 60%), radial-gradient(circle at 25% 80%, ${alpha(C.cerulean, 0.1)} 0%, transparent 50%)`,
          filter: "blur(50px)",
          zIndex: 0,
        }}
      />

      {/* ========== MAIN CONSOLE CARD (anchor) ==========
          Centered horizontally; everything else hugs its edges. */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: "10%", lg: "9%" },
          left: { xs: "4%", lg: "6%" },
          right: { xs: "4%", lg: "6%" },
          background: `linear-gradient(160deg, ${C.inkSoft} 0%, ${C.ink} 100%)`,
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: `0 40px 90px -25px ${alpha("#000", 0.7)},
                      inset 0 0 0 1px ${alpha(C.emeraldGlow, 0.15)}`,
          zIndex: 3,
          opacity: shown ? 1 : 0,
          transform: shown
            ? "translateY(0) rotateX(0deg) rotateY(-1deg)"
            : "translateY(40px) rotateX(6deg) rotateY(-1deg)",
          transition: "all 1.2s cubic-bezier(0.22, 1, 0.36, 1)",
          animation: shown ? "floatA 9s ease-in-out infinite" : "none",
        }}
      >
        {/* Window chrome */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2.5,
            py: 1.5,
            borderBottom: `1px solid ${alpha(C.parchment, 0.06)}`,
            background: alpha("#000", 0.2),
          }}
        >
          <Stack direction="row" spacing={0.75}>
            {[
              alpha(C.emeraldGlow, 0.6),
              alpha(C.emeraldGlow, 0.3),
              alpha(C.emeraldGlow, 0.15),
            ].map((c, i) => (
              <Box
                key={i}
                sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: c }}
              />
            ))}
          </Stack>
          <Typography
            sx={{
              fontFamily: FONT_MONO,
              fontSize: "0.6rem",
              letterSpacing: "0.15em",
              color: alpha(C.parchment, 0.4),
              textTransform: "uppercase",
            }}
          >
            edubook · admin console
          </Typography>
          <Box sx={{ width: 24 }} />
        </Stack>

        {/* Module switcher pills */}
        <Box sx={{ px: 2.5, pt: 2 }}>
          <Stack direction="row" spacing={0.75} sx={{ overflowX: "hidden" }}>
            {modules.map((m, i) => (
              <Box
                key={m.label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.6,
                  px: 1.1,
                  py: 0.55,
                  borderRadius: "999px",
                  background:
                    i === activeModule ? alpha(C.emeraldGlow, 0.18) : "transparent",
                  border: `1px solid ${
                    i === activeModule
                      ? alpha(C.emeraldGlow, 0.4)
                      : alpha(C.parchment, 0.1)
                  }`,
                  transition: "all 0.5s ease",
                }}
              >
                <Box
                  sx={{
                    color:
                      i === activeModule ? C.emeraldGlow : alpha(C.parchment, 0.5),
                    display: "grid",
                    placeItems: "center",
                    transition: "color 0.5s ease",
                  }}
                >
                  {m.icon}
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontFamily: FONT_BODY,
                    fontWeight: 500,
                    color:
                      i === activeModule ? C.emeraldGlow : alpha(C.parchment, 0.55),
                    transition: "color 0.5s ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  {m.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Greeting + LIVE badge */}
        <Box sx={{ px: 2.5, pt: 2.25, pb: 1.75 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={1.75}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: "0.6rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: alpha(C.emeraldGlow, 0.7),
                  mb: 0.4,
                }}
              >
                Spring Term · Mon, May 02
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_DISPLAY,
                  fontSize: { xs: "1rem", sm: "1.15rem" },
                  color: C.parchment,
                  fontWeight: 500,
                  fontStyle: "italic",
                  lineHeight: 1.1,
                }}
              >
                Welcome back, Dr. Khan
              </Typography>
            </Box>
            <Box
              sx={{
                px: 1,
                py: 0.4,
                borderRadius: "999px",
                background: alpha(C.emerald, 0.18),
                border: `1px solid ${alpha(C.emerald, 0.3)}`,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: C.emeraldGlow,
                  animation: "pulse 2s ease-in-out infinite",
                }}
              />
              <Typography
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: "0.58rem",
                  color: C.emeraldGlow,
                  letterSpacing: "0.1em",
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                Live
              </Typography>
            </Box>
          </Stack>

          {/* KPI tiles */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 1.25,
            }}
          >
            {kpis.map((k, i) => (
              <Box
                key={i}
                sx={{
                  p: 1.5,
                  borderRadius: "11px",
                  background: alpha(C.parchment, 0.04),
                  border: `1px solid ${alpha(C.parchment, 0.07)}`,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5}>
                  <Box sx={{ color: alpha(C.emeraldGlow, 0.7), display: "grid" }}>
                    {k.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: FONT_MONO,
                      fontSize: "0.56rem",
                      color: alpha(C.parchment, 0.5),
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                    }}
                  >
                    {k.label}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="baseline" spacing={0.75}>
                  <Typography
                    sx={{
                      fontFamily: FONT_DISPLAY,
                      fontSize: { xs: "1.1rem", sm: "1.35rem" },
                      fontWeight: 600,
                      color: C.parchment,
                      lineHeight: 1,
                    }}
                  >
                    {k.value}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.6rem", color: C.emeraldGlow, fontWeight: 600 }}
                  >
                    {k.delta}
                  </Typography>
                </Stack>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Activity feed */}
        <Box
          sx={{
            mx: 2.5,
            mb: 2.25,
            p: 1.75,
            borderRadius: "12px",
            background: alpha(C.parchment, 0.03),
            border: `1px solid ${alpha(C.parchment, 0.06)}`,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Typography
              sx={{
                fontFamily: FONT_DISPLAY,
                fontStyle: "italic",
                fontSize: "0.85rem",
                color: C.parchment,
                fontWeight: 500,
              }}
            >
              Recent activity
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT_MONO,
                fontSize: "0.55rem",
                color: alpha(C.parchment, 0.4),
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              View all
            </Typography>
          </Stack>

          {[
            {
              icon: <FactCheck sx={{ fontSize: 12 }} />,
              tag: "ATTENDANCE",
              text: "Class IX-A marked · 36 of 38",
              time: "2m",
              color: C.emeraldGlow,
            },
            {
              icon: <EditNote sx={{ fontSize: 12 }} />,
              tag: "PAPER BUILDER",
              text: "Mid-term Maths paper drafted",
              time: "14m",
              color: C.cerulean,
            },
            {
              icon: <AttachMoney sx={{ fontSize: 12 }} />,
              tag: "ACCOUNTS",
              text: "Expense ₨ 18,400 awaiting approval",
              time: "31m",
              color: C.emerald,
            },
          ].map((item, i) => (
            <Stack
              key={i}
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{
                py: 0.75,
                borderTop: i === 0 ? "none" : `1px solid ${alpha(C.parchment, 0.05)}`,
              }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "6px",
                  background: alpha(item.color, 0.15),
                  color: item.color,
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: FONT_MONO,
                    fontSize: "0.52rem",
                    color: alpha(item.color, 0.85),
                    letterSpacing: "0.12em",
                    fontWeight: 600,
                    mb: 0.1,
                  }}
                >
                  {item.tag}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: C.parchment,
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.text}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: "0.6rem",
                  color: alpha(C.parchment, 0.4),
                  flexShrink: 0,
                }}
              >
                {item.time}
              </Typography>
            </Stack>
          ))}
        </Box>
      </Box>

      {/* ========== ACCENT 1: Paper Builder card ==========
          Hugs the TOP-RIGHT corner of the main console (overlapping ~30%). */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: "1%", lg: "0%" },
          right: { xs: "-2%", lg: "-3%" },
          background: C.parchment,
          borderRadius: "16px",
          p: 1.75,
          width: { xs: 175, sm: 195, lg: 215 },
          boxShadow: `0 25px 50px -12px ${alpha("#000", 0.5)}`,
          zIndex: 6,
          opacity: shown ? 1 : 0,
          transform: shown
            ? "translateY(0) rotate(3deg)"
            : "translateY(-30px) rotate(7deg)",
          transition: "all 1.2s 0.2s cubic-bezier(0.22, 1, 0.36, 1)",
          animation: shown ? "floatB 8s ease-in-out infinite" : "none",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25} mb={1.25}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "9px",
              background: `linear-gradient(135deg, ${C.cerulean}, ${C.ceruleanLight})`,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <LibraryBooks sx={{ fontSize: 16, color: "#fff" }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: FONT_MONO,
                fontSize: "0.55rem",
                color: C.textMuted,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                lineHeight: 1,
                mb: 0.35,
              }}
            >
              Paper Builder
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT_DISPLAY,
                fontSize: "0.85rem",
                fontWeight: 600,
                color: C.text,
                lineHeight: 1,
              }}
            >
              Mid-term · Class X
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.5} mb={1}>
          {[1, 1, 1, 1, 1, 0, 0, 0].map((filled, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                height: 4,
                borderRadius: "2px",
                background: filled ? C.cerulean : alpha(C.cerulean, 0.15),
              }}
            />
          ))}
        </Stack>
        <Typography
          sx={{
            fontSize: "0.62rem",
            color: C.textMuted,
            fontFamily: FONT_MONO,
            letterSpacing: "0.05em",
          }}
        >
          5 / 8 questions added
        </Typography>
      </Box>

      {/* ========== ACCENT 2: Today's Classes pill ==========
          Hugs TOP-LEFT corner of the main console. */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: "1%", lg: "1%" },
          left: { xs: "-2%", lg: "-4%" },
          background: `linear-gradient(135deg, ${C.emerald} 0%, ${C.emeraldDeep} 100%)`,
          borderRadius: "14px",
          p: 1.5,
          minWidth: { xs: 130, lg: 150 },
          boxShadow: `0 20px 40px -10px ${alpha(C.emerald, 0.5)}`,
          zIndex: 6,
          opacity: shown ? 1 : 0,
          transform: shown
            ? "translateY(0) rotate(-3deg)"
            : "translateY(-30px) rotate(-7deg)",
          transition: "all 1.2s 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
          animation: shown ? "floatC 9s ease-in-out infinite" : "none",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75} mb={0.75}>
          <EventNote sx={{ fontSize: 13, color: alpha("#fff", 0.85) }} />
          <Typography
            sx={{
              fontFamily: FONT_MONO,
              fontSize: "0.55rem",
              color: alpha("#fff", 0.85),
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            Today's Classes
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography
            sx={{
              fontFamily: FONT_DISPLAY,
              fontSize: "1.6rem",
              fontWeight: 700,
              color: "#fff",
              lineHeight: 1,
            }}
          >
            24
          </Typography>
          <Typography
            sx={{
              fontSize: "0.6rem",
              color: alpha("#fff", 0.85),
              fontFamily: FONT_MONO,
            }}
          >
            8 rooms
          </Typography>
        </Stack>
      </Box>

      {/* ========== ACCENT 3: Leave Request card ==========
          Hugs BOTTOM-LEFT corner of the main console (overlapping ~25%). */}
      <Box
        sx={{
          position: "absolute",
          bottom: { xs: "0%", lg: "-2%" },
          left: { xs: "-2%", lg: "-4%" },
          background: "#fff",
          borderRadius: "16px",
          p: 1.75,
          width: { xs: 195, sm: 210, lg: 225 },
          boxShadow: `0 25px 50px -12px ${alpha("#000", 0.5)}`,
          zIndex: 6,
          opacity: shown ? 1 : 0,
          transform: shown
            ? "translateY(0) rotate(-2deg)"
            : "translateY(40px) rotate(-6deg)",
          transition: "all 1.2s 0.6s cubic-bezier(0.22, 1, 0.36, 1)",
          animation: shown ? "floatD 10s ease-in-out infinite" : "none",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25} mb={1.25}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${C.emerald}, ${C.emeraldDeep})`,
              display: "grid",
              placeItems: "center",
              color: "#fff",
              fontFamily: FONT_DISPLAY,
              fontSize: "0.72rem",
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            SA
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: FONT_MONO,
                fontSize: "0.52rem",
                color: C.textMuted,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                lineHeight: 1,
                mb: 0.3,
              }}
            >
              Leave Request
            </Typography>
            <Typography
              sx={{
                fontSize: "0.82rem",
                fontWeight: 600,
                color: C.text,
                lineHeight: 1,
              }}
            >
              Sara Ahmed
            </Typography>
          </Box>
        </Stack>
        <Typography
          sx={{
            fontSize: "0.65rem",
            color: C.textMuted,
            mb: 1.25,
            lineHeight: 1.4,
          }}
        >
          2 days · medical · May 5–6
        </Typography>
        <Stack direction="row" spacing={0.75}>
          <Box
            sx={{
              flex: 1,
              py: 0.6,
              borderRadius: "8px",
              background: C.emerald,
              color: "#fff",
              textAlign: "center",
              fontSize: "0.68rem",
              fontWeight: 600,
              fontFamily: FONT_BODY,
            }}
          >
            Approve
          </Box>
          <Box
            sx={{
              flex: 1,
              py: 0.6,
              borderRadius: "8px",
              border: `1px solid ${C.hairline}`,
              color: C.textMuted,
              textAlign: "center",
              fontSize: "0.68rem",
              fontWeight: 600,
              fontFamily: FONT_BODY,
            }}
          >
            Decline
          </Box>
        </Stack>
      </Box>

      {/* ========== ACCENT 4: Live class chip ==========
          Hugs BOTTOM-RIGHT corner of the main console. */}
      <Box
        sx={{
          position: "absolute",
          bottom: { xs: "4%", lg: "2%" },
          right: { xs: "-2%", lg: "-3%" },
          background: C.inkDeep,
          borderRadius: "12px",
          px: 1.5,
          py: 1,
          boxShadow: `0 18px 36px -10px ${alpha("#000", 0.5)}`,
          display: "flex",
          alignItems: "center",
          gap: 1,
          zIndex: 6,
          opacity: shown ? 1 : 0,
          transform: shown
            ? "translateY(0) rotate(2deg)"
            : "translateY(20px) rotate(5deg)",
          transition: "all 1.2s 0.8s cubic-bezier(0.22, 1, 0.36, 1)",
          animation: shown ? "floatE 7s ease-in-out infinite" : "none",
          border: `1px solid ${alpha(C.cerulean, 0.3)}`,
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: C.cerulean,
            animation: "pulse 2s ease-in-out infinite",
          }}
        />
        <Typography
          sx={{
            fontSize: "0.65rem",
            color: C.parchment,
            fontFamily: FONT_MONO,
            fontWeight: 500,
            letterSpacing: "0.05em",
          }}
        >
          Sciences Lab · ongoing
        </Typography>
      </Box>
    </Box>
  );
};

/* =====================================================
   MAIN PAGE
   ===================================================== */
const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Inject Google Fonts (Fraunces + Inter + JetBrains Mono)
  useEffect(() => {
    const HREF =
      "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,500;1,9..144,600&family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap";
    if (document.querySelector(`link[href="${HREF}"]`)) return;

    const pre1 = document.createElement("link");
    pre1.rel = "preconnect";
    pre1.href = "https://fonts.googleapis.com";
    const pre2 = document.createElement("link");
    pre2.rel = "preconnect";
    pre2.href = "https://fonts.gstatic.com";
    pre2.crossOrigin = "anonymous";
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = HREF;

    document.head.appendChild(pre1);
    document.head.appendChild(pre2);
    document.head.appendChild(link);
  }, []);

  // Original feature list — preserved as-is
  const features = useMemo(
    () => [
      {
        icon: <People sx={{ fontSize: 22 }} />,
        title: "Student Management",
        description:
          "Complete student records, admissions, and profile management with class history tracking.",
      },
      {
        icon: <CalendarMonth sx={{ fontSize: 22 }} />,
        title: "Attendance Tracking",
        description:
          "Real-time attendance management with monthly reports and scheduled offs.",
      },
      {
        icon: <EmojiEvents sx={{ fontSize: 22 }} />,
        title: "Examination & Results",
        description:
          "Comprehensive exam management with result generation and student performance tracking.",
      },
      {
        icon: <Assessment sx={{ fontSize: 22 }} />,
        title: "Analytics & Reports",
        description:
          "Powerful analytics dashboard with student progress, subject, and class performance insights.",
      },
      {
        icon: <AttachMoney sx={{ fontSize: 22 }} />,
        title: "Expense Management",
        description:
          "Track and manage all school expenses with detailed financial reporting.",
      },
      {
        icon: <Mail sx={{ fontSize: 22 }} />,
        title: "Letter Templates",
        description:
          "Create and manage letter templates for automated document generation.",
      },
      {
        icon: <Lock sx={{ fontSize: 22 }} />,
        title: "Role-Based Access",
        description:
          "Advanced permission system with custom roles and user management.",
      },
      {
        icon: <Policy sx={{ fontSize: 22 }} />,
        title: "Policies & SOPs",
        description:
          "Maintain institutional policies and standard operating procedures.",
      },
      {
        icon: <Description sx={{ fontSize: 22 }} />,
        title: "Notifications",
        description:
          "Real-time notification system to keep everyone informed and connected.",
      },
      {
        icon: <AdminPanelSettings sx={{ fontSize: 22 }} />,
        title: "User Roles & Groups",
        description:
          "Flexible user grouping and role assignment for efficient access control.",
      },
      {
        icon: <AutoStories sx={{ fontSize: 22 }} />,
        title: "And much more...",
        description:
          "Discover the complete suite of tools designed for educational excellence.",
      },
    ],
    []
  );

  const navItems = [
    { label: "Features", section: "features" },
    { label: "Dashboard", section: "preview" },
    { label: "Pricing", section: "pricing" },
    { label: "Contact", section: "contact" },
  ];

  const scrollToSection = (sectionId: string) => {
    const target = sectionId === "demo" ? "demo-form" : sectionId;
    const element = document.getElementById(target);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  /* Logo block — uses /images/edubook-logo.png with a subtle plate
     behind it on dark surfaces. The plate gives elegant contrast
     when the logo is displayed on the dark hero. */
  const LogoMark: React.FC<{ onDark?: boolean; size?: number }> = ({
    onDark,
    size = 44,
  }) => (
    <Box
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        cursor: "pointer",
        userSelect: "none",
        position: "relative",
        transition: "all 0.3s ease",
        "&:hover": { transform: "scale(1.03)" },
      }}
    >
      <Box
        component="img"
        src="/images/edubook-logo.png"
        alt="Edubook"
        sx={{
          height: size,
          width: "auto",
          objectFit: "contain",
          filter: onDark
            ? "drop-shadow(0 4px 12px rgba(0,0,0,0.5))"
            : "drop-shadow(0 2px 8px rgba(15,20,25,0.12))",
        }}
      />
    </Box>
  );

  return (
    <>
      <GlobalStyles
        styles={{
          "html, body": {
            fontFamily: FONT_BODY,
            background: C.parchment,
            color: C.text,
            scrollBehavior: "smooth",
          },
          "@keyframes floatA": {
            "0%, 100%": { transform: "translateY(0) rotate(-1.5deg)" },
            "50%": { transform: "translateY(-8px) rotate(-1.5deg)" },
          },
          "@keyframes floatB": {
            "0%, 100%": { transform: "translateY(0) rotate(2deg)" },
            "50%": { transform: "translateY(10px) rotate(2deg)" },
          },
          "@keyframes floatC": {
            "0%, 100%": { transform: "translateY(0) rotate(-1deg)" },
            "50%": { transform: "translateY(-6px) rotate(-1deg)" },
          },
          "@keyframes floatD": {
            "0%, 100%": { transform: "translateY(0)" },
            "50%": { transform: "translateY(-10px)" },
          },
          "@keyframes floatE": {
            "0%, 100%": { transform: "translateY(0) rotate(2deg)" },
            "50%": { transform: "translateY(-12px) rotate(2deg)" },
          },
          "@keyframes floatF": {
            "0%, 100%": { transform: "translateY(0)" },
            "50%": { transform: "translateY(-8px)" },
          },
          "@keyframes pulse": {
            "0%, 100%": {
              boxShadow: `0 0 0 4px ${alpha(C.cerulean, 0.18)}`,
            },
            "50%": {
              boxShadow: `0 0 0 7px ${alpha(C.cerulean, 0.08)}`,
            },
          },
          "@keyframes fadeUp": {
            from: { opacity: 0, transform: "translateY(20px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          "*::selection": {
            background: C.emerald,
            color: C.parchment,
          },
        }}
      />

      <Box sx={{ minHeight: "100vh", bgcolor: C.parchment, overflow: "hidden" }}>
        {/* ================ NAVBAR ================ */}
        <Box
          component="header"
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            transition: "all 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
            bgcolor: isScrolled ? alpha(C.ink, 0.85) : "transparent",
            backdropFilter: isScrolled ? "blur(20px) saturate(180%)" : "none",
            borderBottom: isScrolled
              ? `1px solid ${alpha(C.emeraldGlow, 0.1)}`
              : "1px solid transparent",
          }}
        >
          <Container maxWidth="xl" sx={{ px: { xs: 2.5, md: 5 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: { xs: "76px", md: "80px" },
              }}
            >
              <LogoMark onDark size={isMobile ? 56 : 80} />

              {!isMobile && (
                <Stack direction="row" spacing={0.5}>
                  {navItems.map((item) => (
                    <Button
                      key={item.section}
                      onClick={() => scrollToSection(item.section)}
                      disableRipple
                      sx={{
                        color: C.parchment,
                        fontFamily: FONT_BODY,
                        fontWeight: 500,
                        fontSize: "0.92rem",
                        textTransform: "none",
                        letterSpacing: "0.01em",
                        px: 2,
                        py: 1,
                        position: "relative",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          left: "50%",
                          bottom: 6,
                          width: 0,
                          height: "1px",
                          background: C.emeraldGlow,
                          transform: "translateX(-50%)",
                          transition: "width 0.35s ease",
                        },
                        "&:hover": {
                          background: "transparent",
                          color: C.emeraldGlow,
                          "&::after": { width: "60%" },
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  ))}
                </Stack>
              )}

              {!isMobile ? (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Button
                    onClick={() => navigate("/login")}
                    disableRipple
                    sx={{
                      color: C.parchment,
                      fontFamily: FONT_BODY,
                      fontWeight: 500,
                      fontSize: "0.92rem",
                      textTransform: "none",
                      px: 2,
                      "&:hover": {
                        background: "transparent",
                        color: C.emeraldGlow,
                      },
                    }}
                  >
                    Sign in
                  </Button>
                  <Button
                    onClick={() => scrollToSection("demo")}
                    disableRipple
                    sx={{
                      bgcolor: C.parchment,
                      color: C.ink,
                      fontFamily: FONT_BODY,
                      fontWeight: 500,
                      fontSize: "0.92rem",
                      textTransform: "none",
                      px: 3,
                      py: 1.25,
                      borderRadius: "999px",
                      letterSpacing: "0.01em",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: C.emeraldGlow,
                        color: C.ink,
                        transform: "translateY(-1px)",
                        boxShadow: `0 8px 20px ${alpha(C.emeraldGlow, 0.3)}`,
                      },
                    }}
                  >
                    Book a Demo
                  </Button>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    onClick={() => navigate("/login")}
                    disableRipple
                    sx={{
                      color: C.parchment,
                      fontFamily: FONT_BODY,
                      fontWeight: 500,
                      fontSize: "0.85rem",
                      textTransform: "none",
                      px: 1.5,
                    }}
                  >
                    Sign in
                  </Button>
                  <IconButton
                    onClick={() => setMobileMenuOpen(true)}
                    sx={{
                      color: C.parchment,
                      border: `1px solid ${alpha(C.parchment, 0.2)}`,
                      borderRadius: "10px",
                    }}
                  >
                    <MenuIcon />
                  </IconButton>
                </Stack>
              )}
            </Box>
          </Container>
        </Box>

        {/* ================ MOBILE DRAWER ================ */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          sx={{
            "& .MuiDrawer-paper": {
              width: "82%",
              maxWidth: 340,
              bgcolor: C.ink,
              backgroundImage: "none",
              color: C.parchment,
            },
          }}
        >
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column", p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
              <LogoMark onDark size={48} />
              <IconButton onClick={() => setMobileMenuOpen(false)}>
                <CloseIcon sx={{ color: C.parchment }} />
              </IconButton>
            </Stack>
            <List sx={{ flex: 1 }}>
              {navItems.map((item) => (
                <ListItem key={item.section} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      scrollToSection(item.section);
                      setMobileMenuOpen(false);
                    }}
                    sx={{
                      py: 1.75,
                      px: 0,
                      borderBottom: `1px solid ${alpha(C.parchment, 0.1)}`,
                      "&:hover": { background: "transparent" },
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontFamily: FONT_DISPLAY,
                        fontSize: "1.4rem",
                        fontWeight: 500,
                        color: C.parchment,
                      }}
                    />
                    <ArrowOutward sx={{ fontSize: 18, color: C.emeraldGlow }} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            <Button
              fullWidth
              onClick={() => {
                scrollToSection("demo");
                setMobileMenuOpen(false);
              }}
              sx={{
                bgcolor: C.parchment,
                color: C.ink,
                fontFamily: FONT_BODY,
                fontWeight: 600,
                textTransform: "none",
                py: 1.75,
                borderRadius: "999px",
                fontSize: "1rem",
                "&:hover": { bgcolor: C.emeraldGlow },
              }}
            >
              Book a Demo
            </Button>
          </Box>
        </Drawer>

        {/* ================ HERO ================ */}
        <Box
          component="section"
          sx={{
            position: "relative",
            pt: { xs: "120px", md: "150px" },
            pb: { xs: 8, md: 12 },
            background: `linear-gradient(180deg, ${C.ink} 0%, ${C.inkSoft} 100%)`,
            color: C.parchment,
            overflow: "hidden",
          }}
        >
          {/* Subtle grid texture */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundImage: `radial-gradient(${alpha(
                C.parchment,
                0.04
              )} 1px, transparent 1px)`,
              backgroundSize: "32px 32px",
              maskImage:
                "radial-gradient(ellipse at center, black 30%, transparent 75%)",
              WebkitMaskImage:
                "radial-gradient(ellipse at center, black 30%, transparent 75%)",
              pointerEvents: "none",
            }}
          />
          {/* Soft accent blobs */}
          <Box
            sx={{
              position: "absolute",
              top: "-15%",
              right: "-10%",
              width: 600,
              height: 600,
              background: `radial-gradient(circle, ${alpha(
                C.emerald,
                0.25
              )} 0%, transparent 70%)`,
              filter: "blur(60px)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "-20%",
              left: "-10%",
              width: 500,
              height: 500,
              background: `radial-gradient(circle, ${alpha(
                C.cerulean,
                0.18
              )} 0%, transparent 70%)`,
              filter: "blur(60px)",
              pointerEvents: "none",
            }}
          />

          <Container
            maxWidth="xl"
            sx={{ px: { xs: 2.5, md: 5 }, position: "relative" }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "1.05fr 1fr" },
                gap: { xs: 6, lg: 7 },
                alignItems: "center",
              }}
            >
              <Box sx={{ animation: "fadeUp 1s cubic-bezier(0.22, 1, 0.36, 1) both" }}>
                <Box mb={3.5}>
                  <Eyebrow light>School Operating System · Est. 2026</Eyebrow>
                </Box>

                <Typography
                  component="h1"
                  sx={{
                    fontFamily: FONT_DISPLAY,
                    fontSize: {
                      xs: "2.4rem",
                      sm: "3.3rem",
                      md: "3.9rem",
                      lg: "4.4rem",
                    },
                    fontWeight: 500,
                    color: C.parchment,
                    lineHeight: 1.04,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Run your institution
                  <Box
                    component="span"
                    sx={{
                      fontStyle: "italic",
                      color: C.emeraldGlow,
                      fontWeight: 400,
                    }}
                  >
                    {" "}
                    with grace.
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    mt: 3.5,
                    fontSize: { xs: "1.05rem", md: "1.15rem" },
                    color: C.textOnDarkMuted,
                    maxWidth: 520,
                    lineHeight: 1.7,
                  }}
                >
                  Admissions, attendance, examinations, paper builder, finance,
                  and analytics — every workflow your school runs on,
                  composed into one quietly powerful platform.
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  mt={4.5}
                  alignItems={{ xs: "stretch", sm: "center" }}
                >
                  <Button
                    onClick={() => scrollToSection("demo")}
                    disableRipple
                    sx={{
                      bgcolor: C.parchment,
                      color: C.ink,
                      fontFamily: FONT_BODY,
                      fontWeight: 600,
                      fontSize: "1rem",
                      textTransform: "none",
                      px: 4,
                      py: 1.75,
                      borderRadius: "999px",
                      transition: "all 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
                      "& .arrow": {
                        ml: 1,
                        fontSize: 18,
                        transition: "transform 0.35s ease",
                      },
                      "&:hover": {
                        bgcolor: C.emeraldGlow,
                        color: C.ink,
                        transform: "translateY(-2px)",
                        boxShadow: `0 14px 30px -8px ${alpha(C.emeraldGlow, 0.5)}`,
                        "& .arrow": { transform: "translateX(4px)" },
                      },
                    }}
                  >
                    Request a demo
                    <ArrowForward className="arrow" />
                  </Button>
                  <Button
                    onClick={() => scrollToSection("features")}
                    disableRipple
                    sx={{
                      color: C.parchment,
                      fontFamily: FONT_BODY,
                      fontWeight: 500,
                      fontSize: "1rem",
                      textTransform: "none",
                      px: 3,
                      py: 1.75,
                      borderRadius: "999px",
                      border: `1px solid ${alpha(C.parchment, 0.25)}`,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: C.emeraldGlow,
                        color: C.emeraldGlow,
                        background: alpha(C.emeraldGlow, 0.05),
                      },
                    }}
                  >
                    See features
                  </Button>
                </Stack>

                {/* Inline product capability tags — gives "what it does" at a glance */}
                <Stack
                  direction="row"
                  spacing={1}
                  mt={5}
                  sx={{
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  {[
                    "Admissions",
                    "Attendance",
                    "Paper Builder",
                    "Question Bank",
                    "Analytics",
                    "Finance",
                  ].map((tag) => (
                    <Box
                      key={tag}
                      sx={{
                        px: 1.5,
                        py: 0.6,
                        borderRadius: "999px",
                        border: `1px solid ${alpha(C.parchment, 0.15)}`,
                        background: alpha(C.parchment, 0.03),
                        fontFamily: FONT_MONO,
                        fontSize: "0.7rem",
                        color: C.textOnDarkMuted,
                        letterSpacing: "0.05em",
                        transition: "all 0.25s ease",
                        "&:hover": {
                          borderColor: alpha(C.emeraldGlow, 0.4),
                          color: C.emeraldGlow,
                          background: alpha(C.emeraldGlow, 0.08),
                        },
                      }}
                    >
                      {tag}
                    </Box>
                  ))}
                </Stack>
              </Box>

              <HeroComposition />
            </Box>
          </Container>
        </Box>

        {/* ================ FEATURES ================ */}
        <Box
          id="features"
          sx={{
            py: { xs: 9, md: 13 },
            bgcolor: C.parchment,
            position: "relative",
          }}
        >
          <Container maxWidth="xl" sx={{ px: { xs: 2.5, md: 5 } }}>
            {/* Header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1.1fr" },
                gap: { xs: 3, md: 8 },
                mb: { xs: 5, md: 8 },
                alignItems: "end",
              }}
            >
              <Box>
                <Box mb={2.5}>
                  <Eyebrow>Comprehensive Suite</Eyebrow>
                </Box>
                <Typography
                  sx={{
                    fontFamily: FONT_DISPLAY,
                    fontSize: { xs: "2rem", sm: "2.6rem", md: "3rem" },
                    fontWeight: 500,
                    color: C.text,
                    lineHeight: 1.05,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Every workflow your
                  <br />
                  institution{" "}
                  <Box
                    component="span"
                    sx={{
                      fontStyle: "italic",
                      color: C.emerald,
                      fontWeight: 400,
                    }}
                  >
                    needs.
                  </Box>
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: { xs: "0.98rem", md: "1.05rem" },
                  color: C.textMuted,
                  lineHeight: 1.7,
                  maxWidth: 480,
                }}
              >
                Each module works beautifully on its own — and together they
                form an operating system for the modern educational
                institution.
              </Typography>
            </Box>

            {/* Compact feature grid — smaller cards, smooth hover */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: 2,
              }}
            >
              {features.map((f, i) => (
                <Box
                  key={i}
                  sx={{
                    position: "relative",
                    p: 2.5,
                    borderRadius: "14px",
                    background: "#fff",
                    border: `1px solid ${C.hairline}`,
                    overflow: "hidden",
                    cursor: "default",
                    transition:
                      "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.5s ease, border-color 0.5s ease",

                    /* Background sheet that fades in from below */
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      background: `linear-gradient(180deg, ${C.ink} 0%, ${C.inkSoft} 100%)`,
                      opacity: 0,
                      transition: "opacity 0.5s ease",
                      zIndex: 0,
                    },

                    /* Subtle accent corner that grows on hover */
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: 60,
                      height: 60,
                      background: `radial-gradient(circle at top right, ${alpha(
                        C.emeraldGlow,
                        0.4
                      )} 0%, transparent 70%)`,
                      opacity: 0,
                      transition: "opacity 0.6s ease",
                      zIndex: 1,
                    },

                    "& > *": { position: "relative", zIndex: 2 },

                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: `0 18px 40px -12px ${alpha(C.ink, 0.35)}`,
                      borderColor: C.ink,
                      "&::before": { opacity: 1 },
                      "&::after": { opacity: 1 },
                      "& .feat-icon": {
                        background: alpha(C.emeraldGlow, 0.15),
                        borderColor: alpha(C.emeraldGlow, 0.4),
                        color: C.emeraldGlow,
                      },
                      "& .feat-num": { color: alpha(C.emeraldGlow, 0.6) },
                      "& .feat-title": { color: C.parchment },
                      "& .feat-desc": { color: C.textOnDarkMuted },
                      "& .feat-arrow": {
                        opacity: 1,
                        transform: "translate(2px, -2px)",
                        color: C.emeraldGlow,
                      },
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={2}
                  >
                    <Box
                      className="feat-icon"
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        background: alpha(C.emerald, 0.08),
                        border: `1px solid ${alpha(C.emerald, 0.18)}`,
                        color: C.emerald,
                        display: "grid",
                        placeItems: "center",
                        transition: "all 0.5s ease",
                      }}
                    >
                      {f.icon}
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography
                        className="feat-num"
                        sx={{
                          fontFamily: FONT_MONO,
                          fontSize: "0.65rem",
                          color: alpha(C.text, 0.3),
                          letterSpacing: "0.1em",
                          transition: "color 0.5s ease",
                        }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </Typography>
                      <ArrowOutward
                        className="feat-arrow"
                        sx={{
                          fontSize: 14,
                          color: alpha(C.text, 0.3),
                          opacity: 0,
                          transition: "all 0.5s ease",
                        }}
                      />
                    </Stack>
                  </Stack>
                  <Typography
                    className="feat-title"
                    sx={{
                      fontFamily: FONT_DISPLAY,
                      fontSize: "1.05rem",
                      fontWeight: 600,
                      color: C.text,
                      mb: 0.75,
                      lineHeight: 1.25,
                      transition: "color 0.5s ease",
                    }}
                  >
                    {f.title}
                  </Typography>
                  <Typography
                    className="feat-desc"
                    sx={{
                      fontSize: "0.82rem",
                      color: C.textMuted,
                      lineHeight: 1.55,
                      transition: "color 0.5s ease",
                    }}
                  >
                    {f.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>

        {/* ================ DASHBOARD PREVIEW ================ */}
        <Box
          id="preview"
          sx={{
            py: { xs: 9, md: 13 },
            bgcolor: C.parchmentSoft,
            position: "relative",
          }}
        >
          <Container maxWidth="lg" sx={{ px: { xs: 2.5, md: 5 } }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1.15fr" },
                gap: { xs: 5, md: 7 },
                alignItems: "center",
              }}
            >
              <Box sx={{ order: { xs: 2, md: 1 } }}>
                <Box mb={2.5}>
                  <Eyebrow>The Dashboard</Eyebrow>
                </Box>
                <Typography
                  sx={{
                    fontFamily: FONT_DISPLAY,
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "2.85rem" },
                    fontWeight: 500,
                    color: C.text,
                    lineHeight: 1.05,
                    letterSpacing: "-0.02em",
                    mb: 2.5,
                  }}
                >
                  Intuitive
                  <Box
                    component="span"
                    sx={{
                      fontStyle: "italic",
                      color: C.emerald,
                      fontWeight: 400,
                    }}
                  >
                    {" "}
                    by design.
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "1rem", md: "1.05rem" },
                    color: C.textMuted,
                    lineHeight: 1.7,
                    mb: 3.5,
                    maxWidth: 480,
                  }}
                >
                  A clean, considered interface ensures that teachers,
                  students, and administrators can navigate the platform with
                  ease — reducing training time and increasing adoption.
                </Typography>
                <Button
                  onClick={() => navigate("/login")}
                  disableRipple
                  sx={{
                    bgcolor: C.ink,
                    color: C.parchment,
                    fontFamily: FONT_BODY,
                    fontWeight: 500,
                    fontSize: "0.95rem",
                    textTransform: "none",
                    px: 3.5,
                    py: 1.5,
                    borderRadius: "999px",
                    "&:hover": {
                      bgcolor: C.inkDeep,
                      transform: "translateY(-2px)",
                      boxShadow: `0 12px 28px ${alpha(C.ink, 0.3)}`,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Explore Dashboard
                  <ArrowOutward sx={{ fontSize: 16, ml: 1 }} />
                </Button>
              </Box>

              {/* Original dashboard image, framed elegantly */}
              <Box
                sx={{
                  order: { xs: 1, md: 2 },
                  position: "relative",
                  borderRadius: "20px",
                  overflow: "hidden",
                  background: "#fff",
                  border: `1px solid ${C.hairline}`,
                  boxShadow: `0 30px 70px -20px ${alpha(C.ink, 0.25)}`,
                  transition: "transform 0.5s ease",
                  p: 1.5,
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `0 40px 90px -20px ${alpha(C.ink, 0.35)}`,
                  },
                }}
              >
                <Box
                  component="img"
                  src="/images/analysis.png"
                  alt="Edubook Dashboard"
                  sx={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    borderRadius: "12px",
                    display: "block",
                  }}
                />
              </Box>
            </Box>
          </Container>
        </Box>

        {/* ================ PRICING ================ */}
        <Box
          id="pricing"
          sx={{
            py: { xs: 9, md: 13 },
            bgcolor: C.parchment,
          }}
        >
          <Container maxWidth="lg" sx={{ px: { xs: 2.5, md: 5 } }}>
            <Box sx={{ textAlign: "center", mb: { xs: 5, md: 8 } }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
                <Eyebrow>Investment</Eyebrow>
              </Box>
              <Typography
                sx={{
                  fontFamily: FONT_DISPLAY,
                  fontSize: { xs: "2rem", sm: "2.6rem", md: "3rem" },
                  fontWeight: 500,
                  color: C.text,
                  lineHeight: 1.05,
                  letterSpacing: "-0.02em",
                  mb: 2,
                }}
              >
                Simple, transparent
                <Box
                  component="span"
                  sx={{
                    fontStyle: "italic",
                    color: C.emerald,
                    fontWeight: 400,
                  }}
                >
                  {" "}
                  pricing.
                </Box>
              </Typography>
              <Typography
                sx={{
                  color: C.textMuted,
                  fontSize: { xs: "1rem", md: "1.05rem" },
                  maxWidth: 540,
                  mx: "auto",
                  lineHeight: 1.7,
                }}
              >
                Two clear plans. Cancel any time. No surprises.
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 0,
                maxWidth: 920,
                mx: "auto",
                bgcolor: "#fff",
                borderRadius: "20px",
                overflow: "hidden",
                border: `1px solid ${C.hairline}`,
                boxShadow: `0 25px 70px -25px ${alpha(C.ink, 0.18)}`,
              }}
            >
              {/* Monthly */}
              <Box
                sx={{
                  p: { xs: 4, md: 5 },
                  borderRight: { md: `1px solid ${C.hairline}` },
                  borderBottom: { xs: `1px solid ${C.hairline}`, md: "none" },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FONT_MONO,
                    fontSize: "0.7rem",
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: C.textMuted,
                    mb: 2,
                  }}
                >
                  Monthly
                </Typography>
                <Stack direction="row" alignItems="baseline" spacing={1} mb={1}>
                  <Typography
                    sx={{
                      fontFamily: FONT_DISPLAY,
                      fontSize: { xs: "2.8rem", md: "3.5rem" },
                      fontWeight: 600,
                      color: C.text,
                      lineHeight: 1,
                    }}
                  >
                    $100
                  </Typography>
                  <Typography sx={{ color: C.textMuted, fontSize: "1rem" }}>
                    / month
                  </Typography>
                </Stack>
                <Typography
                  sx={{ color: C.textMuted, fontSize: "0.92rem", mb: 3.5 }}
                >
                  Full access, pay as you go.
                </Typography>
                <Stack spacing={1.5} mb={3.5}>
                  {[
                    "All Features Included",
                    "Unlimited Users",
                    "Premium Support",
                    "Continuous Updates",
                  ].map((item, i) => (
                    <Stack key={i} direction="row" spacing={1.25} alignItems="center">
                      <CheckCircle sx={{ fontSize: 18, color: C.emerald }} />
                      <Typography sx={{ color: C.text, fontSize: "0.92rem" }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
                <Button
                  fullWidth
                  onClick={() => scrollToSection("demo")}
                  disableRipple
                  sx={{
                    bgcolor: "transparent",
                    color: C.ink,
                    fontFamily: FONT_BODY,
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    textTransform: "none",
                    border: `1px solid ${C.ink}`,
                    py: 1.5,
                    borderRadius: "999px",
                    "&:hover": {
                      bgcolor: C.ink,
                      color: C.parchment,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Choose Monthly
                </Button>
              </Box>

              {/* Yearly — featured */}
              <Box
                sx={{
                  p: { xs: 4, md: 5 },
                  bgcolor: C.ink,
                  color: C.parchment,
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: { xs: 16, md: 24 },
                    right: { xs: 16, md: 24 },
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "999px",
                    background: C.emeraldGlow,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: FONT_MONO,
                      fontSize: "0.65rem",
                      color: C.ink,
                      letterSpacing: "0.15em",
                      textTransform: "uppercase",
                      fontWeight: 700,
                    }}
                  >
                    Save 17%
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontFamily: FONT_MONO,
                    fontSize: "0.7rem",
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: alpha(C.parchment, 0.6),
                    mb: 2,
                  }}
                >
                  Yearly
                </Typography>
                <Stack direction="row" alignItems="baseline" spacing={1} mb={1}>
                  <Typography
                    sx={{
                      fontFamily: FONT_DISPLAY,
                      fontSize: { xs: "2.8rem", md: "3.5rem" },
                      fontWeight: 600,
                      color: C.parchment,
                      lineHeight: 1,
                    }}
                  >
                    $1,000
                  </Typography>
                  <Typography sx={{ color: alpha(C.parchment, 0.6), fontSize: "1rem" }}>
                    / year
                  </Typography>
                </Stack>
                <Typography
                  sx={{
                    color: alpha(C.parchment, 0.7),
                    fontSize: "0.92rem",
                    mb: 3.5,
                  }}
                >
                  Save big with an annual plan.
                </Typography>
                <Stack spacing={1.5} mb={3.5}>
                  {[
                    "All Features Included",
                    "Unlimited Users",
                    "Priority Support",
                    "Continuous Updates",
                  ].map((item, i) => (
                    <Stack key={i} direction="row" spacing={1.25} alignItems="center">
                      <CheckCircleOutline sx={{ fontSize: 18, color: C.emeraldGlow }} />
                      <Typography sx={{ color: C.parchment, fontSize: "0.92rem" }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
                <Button
                  fullWidth
                  onClick={() => scrollToSection("demo")}
                  disableRipple
                  sx={{
                    bgcolor: C.emeraldGlow,
                    color: C.ink,
                    fontFamily: FONT_BODY,
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    textTransform: "none",
                    py: 1.5,
                    borderRadius: "999px",
                    "&:hover": {
                      bgcolor: C.parchment,
                      transform: "translateY(-1px)",
                      boxShadow: `0 10px 24px ${alpha(C.emeraldGlow, 0.4)}`,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Choose Yearly
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* ================ CONTACT ================ */}
        <Box
          id="contact"
          sx={{
            py: { xs: 9, md: 13 },
            bgcolor: C.parchment,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Container maxWidth="lg" sx={{ px: { xs: 2.5, md: 5 } }}>
            <Box
              sx={{
                p: { xs: 3, md: 7 },
                borderRadius: "22px",
                background: `linear-gradient(135deg, ${C.ink} 0%, ${C.inkDeep} 100%)`,
                position: "relative",
                overflow: "hidden",
                color: C.parchment,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: -60,
                  right: -60,
                  width: 240,
                  height: 240,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${alpha(
                    C.emerald,
                    0.4
                  )} 0%, transparent 70%)`,
                  filter: "blur(40px)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: -60,
                  left: -60,
                  width: 240,
                  height: 240,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${alpha(
                    C.cerulean,
                    0.25
                  )} 0%, transparent 70%)`,
                  filter: "blur(40px)",
                }}
              />

              <Box
                sx={{
                  position: "relative",
                  display: "grid",
                  gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1.2fr) minmax(0, 1fr)" },
                  gap: { xs: 4, md: 7 },
                  alignItems: "center",
                }}
              >
                <Box>
                  <Box mb={2.5}>
                    <Eyebrow light>Get in Touch</Eyebrow>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: FONT_DISPLAY,
                      fontSize: { xs: "1.85rem", sm: "2.4rem", md: "2.8rem" },
                      fontWeight: 500,
                      color: C.parchment,
                      lineHeight: 1.05,
                      letterSpacing: "-0.02em",
                      mb: 2.5,
                    }}
                  >
                    Begin a
                    <Box
                      component="span"
                      sx={{
                        fontStyle: "italic",
                        color: C.emeraldGlow,
                        fontWeight: 400,
                      }}
                    >
                      {" "}
                      conversation.
                    </Box>
                  </Typography>
                  <Typography
                    sx={{
                      color: C.textOnDarkMuted,
                      fontSize: { xs: "0.98rem", md: "1.05rem" },
                      lineHeight: 1.7,
                      maxWidth: 460,
                    }}
                  >
                    We'd love to learn about your institution. Reach out and a
                    member of our team will respond within one business day.
                  </Typography>
                </Box>

                <Stack spacing={2}>
                  <Box
                    component="a"
                    href="tel:+923318957407"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 1.5, md: 2 },
                      p: { xs: 2, md: 2.5 },
                      borderRadius: "12px",
                      background: alpha(C.parchment, 0.05),
                      border: `1px solid ${alpha(C.parchment, 0.1)}`,
                      textDecoration: "none",
                      color: "inherit",
                      transition: "all 0.3s ease",
                      minWidth: 0,
                      "&:hover": {
                        background: alpha(C.emeraldGlow, 0.1),
                        borderColor: alpha(C.emeraldGlow, 0.3),
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        background: alpha(C.emeraldGlow, 0.15),
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Phone sx={{ fontSize: 18, color: C.emeraldGlow }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          fontFamily: FONT_MONO,
                          letterSpacing: "0.15em",
                          textTransform: "uppercase",
                          color: alpha(C.parchment, 0.5),
                          mb: 0.4,
                        }}
                      >
                        Call us
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: FONT_DISPLAY,
                          fontSize: { xs: "0.95rem", md: "1.05rem" },
                          color: C.parchment,
                          whiteSpace: "nowrap",
                        }}
                      >
                        +92 331 8957407
                      </Typography>
                    </Box>
                    <ArrowOutward sx={{ fontSize: 16, color: alpha(C.parchment, 0.4), flexShrink: 0, display: { xs: "none", sm: "block" } }} />
                  </Box>

                  <Box
                    component="a"
                    href="mailto:theedubookapp@gmail.com"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 1.5, md: 2 },
                      p: { xs: 2, md: 2.5 },
                      borderRadius: "12px",
                      background: alpha(C.parchment, 0.05),
                      border: `1px solid ${alpha(C.parchment, 0.1)}`,
                      textDecoration: "none",
                      color: "inherit",
                      transition: "all 0.3s ease",
                      minWidth: 0,
                      "&:hover": {
                        background: alpha(C.emeraldGlow, 0.1),
                        borderColor: alpha(C.emeraldGlow, 0.3),
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        background: alpha(C.emeraldGlow, 0.15),
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Email sx={{ fontSize: 18, color: C.emeraldGlow }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          fontFamily: FONT_MONO,
                          letterSpacing: "0.15em",
                          textTransform: "uppercase",
                          color: alpha(C.parchment, 0.5),
                          mb: 0.4,
                        }}
                      >
                        Email us
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: FONT_DISPLAY,
                          fontSize: { xs: "0.95rem", md: "1.05rem" },
                          color: C.parchment,
                          whiteSpace: "nowrap",
                        }}
                      >
                        theedubookapp@gmail.com
                      </Typography>
                    </Box>
                    <ArrowOutward sx={{ fontSize: 16, color: alpha(C.parchment, 0.4), flexShrink: 0, display: { xs: "none", sm: "block" } }} />
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Demo form (existing component) */}
        <RequestDemoForm />

        {/* ================ FOOTER ================ */}
        <Box
          component="footer"
          sx={{
            bgcolor: C.inkDeep,
            color: alpha(C.parchment, 0.7),
            pt: 9,
            pb: 4,
          }}
        >
          <Container maxWidth="xl" sx={{ px: { xs: 2.5, md: 5 } }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "1.4fr repeat(4, 1fr)",
                },
                gap: { xs: 5, md: 6 },
                pb: 5,
                borderBottom: `1px solid ${alpha(C.emeraldGlow, 0.12)}`,
              }}
            >
              <Box>
                <Box mb={2.5}>
                  <LogoMark onDark size={72} />
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.92rem",
                    color: alpha(C.parchment, 0.6),
                    lineHeight: 1.7,
                    maxWidth: 280,
                    mb: 2.5,
                  }}
                >
                  Simplifying school management — for educators, by educators.
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT_MONO,
                    fontSize: "0.7rem",
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: C.emeraldGlow,
                  }}
                >
                  Karachi · Worldwide
                </Typography>
              </Box>

              {(
                [
                  {
                    title: "Product",
                    items: [
                      { label: "Features", action: () => scrollToSection("features") },
                      { label: "Dashboard", action: () => scrollToSection("preview") },
                      { label: "Pricing", action: () => scrollToSection("pricing") },
                      { label: "Demo", action: () => scrollToSection("demo") },
                    ],
                  },
                  {
                    title: "Company",
                    items: [
                      { label: "About Us" },
                      { label: "Careers" },
                      { label: "Contact", action: () => scrollToSection("contact") },
                    ],
                  },
                  {
                    title: "Resources",
                    items: [
                      { label: "Blog" },
                      { label: "Help Center" },
                      { label: "Webinars" },
                    ],
                  },
                  {
                    title: "Legal",
                    items: [
                      { label: "Terms of Service" },
                      { label: "Privacy Policy" },
                    ],
                  },
                ] as Array<{
                  title: string;
                  items: Array<{ label: string; action?: () => void }>;
                }>
              ).map((col) => (
                <Box key={col.title}>
                  <Typography
                    sx={{
                      fontFamily: FONT_MONO,
                      fontSize: "0.7rem",
                      letterSpacing: "0.2em",
                      textTransform: "uppercase",
                      color: C.emeraldGlow,
                      mb: 2.5,
                    }}
                  >
                    {col.title}
                  </Typography>
                  <Stack spacing={1.25} alignItems="flex-start">
                    {col.items.map((it) => {
                      const isActive = !!it.action;
                      return (
                        <Box
                          key={it.label}
                          component={isActive ? "button" : "span"}
                          onClick={isActive ? it.action : undefined}
                          sx={{
                            background: "none",
                            border: "none",
                            p: 0,
                            font: "inherit",
                            textAlign: "left",
                            fontSize: "0.9rem",
                            color: isActive
                              ? alpha(C.parchment, 0.75)
                              : alpha(C.parchment, 0.4),
                            cursor: isActive ? "pointer" : "default",
                            transition: "color 0.2s",
                            position: "relative",
                            "&:hover": isActive
                              ? {
                                  color: C.emeraldGlow,
                                }
                              : undefined,
                          }}
                        >
                          {it.label}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              sx={{ pt: 4 }}
            >
              <Typography sx={{ fontSize: "0.85rem", color: alpha(C.parchment, 0.5) }}>
                © {new Date().getFullYear()} Edubook. All rights reserved.
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_DISPLAY,
                  fontStyle: "italic",
                  fontSize: "0.9rem",
                  color: alpha(C.emeraldGlow, 0.7),
                }}
              >
                Crafted for institutions that care.
              </Typography>
            </Stack>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default LandingPage;